import { newElementWith } from "@excalidraw/element";
import { CaptureUpdateAction } from "@excalidraw/excalidraw";

import { applyMindmapLayout } from "./drawolaMindmapLayout";
import {
  findChildren,
  getMindmapData,
  isMindmapNode,
  type MindmapNodeElement,
} from "./drawolaMindmapModel";
import { MINDMAP_DATA_KEY, type MindmapNodeData } from "./types";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

const isInsideBBox = (
  point: { x: number; y: number },
  el: { x: number; y: number; width: number; height: number },
) =>
  point.x >= el.x &&
  point.x <= el.x + el.width &&
  point.y >= el.y &&
  point.y <= el.y + el.height;

// Set of node IDs in the dragged subtree (so we can exclude them as drop
// targets — dropping a node onto its own descendant would create a cycle).
const collectDescendants = (
  elements: readonly ExcalidrawElement[],
  root: MindmapNodeElement,
): Set<string> => {
  const out = new Set<string>([root.id]);
  const queue: MindmapNodeElement[] = [root];
  while (queue.length > 0) {
    const node = queue.shift()!;
    for (const child of findChildren(elements, node)) {
      if (!out.has(child.id)) {
        out.add(child.id);
        queue.push(child);
      }
    }
  }
  return out;
};

// Find the mindmap node whose bbox contains the dragged node's center, while
// excluding the dragged node itself, its descendants, and its existing
// parent (no-op reparent).
export const findReparentTarget = (
  elements: readonly ExcalidrawElement[],
  dragged: MindmapNodeElement,
): MindmapNodeElement | null => {
  const draggedData = dragged.customData[MINDMAP_DATA_KEY];
  const blocked = collectDescendants(elements, dragged);
  const center = {
    x: dragged.x + dragged.width / 2,
    y: dragged.y + dragged.height / 2,
  };

  for (const el of elements) {
    if (!isMindmapNode(el)) {
      continue;
    }
    if (blocked.has(el.id)) {
      continue;
    }
    const data = el.customData[MINDMAP_DATA_KEY];
    if (data.mindmapId !== draggedData.mindmapId) {
      // Dropping across mindmaps is out of scope for now.
      continue;
    }
    if (data.nodeId === draggedData.parentId) {
      // Already child of this node; no reparent needed.
      continue;
    }
    if (isInsideBBox(center, el)) {
      return el;
    }
  }
  return null;
};

const computeSideForNewParent = (
  dragged: MindmapNodeElement,
  newParent: MindmapNodeElement,
): "L" | "R" => {
  const parentData = newParent.customData[MINDMAP_DATA_KEY];
  if (parentData.parentId === null) {
    // New parent is root — pick side based on dragged's current center.
    const draggedCenterX = dragged.x + dragged.width / 2;
    const rootCenterX = newParent.x + newParent.width / 2;
    return draggedCenterX >= rootCenterX ? "R" : "L";
  }
  // New parent is non-root: subtree continues the parent's side.
  return parentData.side === "L" ? "L" : "R";
};

// Walk the dragged subtree and produce an updated customData for each node
// with the new side (children continue the side of their new branch).
const propagateSideToSubtree = (
  elements: readonly ExcalidrawElement[],
  root: MindmapNodeElement,
  newSide: "L" | "R",
): Map<string, MindmapNodeData> => {
  const out = new Map<string, MindmapNodeData>();
  const queue: MindmapNodeElement[] = [root];
  while (queue.length > 0) {
    const node = queue.shift()!;
    const data = node.customData[MINDMAP_DATA_KEY];
    out.set(node.id, { ...data, side: newSide });
    for (const child of findChildren(elements, node)) {
      queue.push(child);
    }
  }
  return out;
};

// Find the arrow that connects `oldParent` -> `dragged`. Used to either
// remove or rebind it on reparent.
const findIncomingArrow = (
  elements: readonly ExcalidrawElement[],
  dragged: MindmapNodeElement,
): ExcalidrawElement | null => {
  const draggedData = dragged.customData[MINDMAP_DATA_KEY];
  if (!draggedData.parentId) {
    return null;
  }
  for (const el of elements) {
    if (
      el.type === "arrow" &&
      !el.isDeleted &&
      el.endBinding?.elementId === dragged.id
    ) {
      return el;
    }
  }
  return null;
};

const removeBoundArrow = (
  el: ExcalidrawElement,
  arrowId: string,
): ExcalidrawElement => {
  if (!el.boundElements) {
    return el;
  }
  const filtered = el.boundElements.filter(
    (b) => !(b.type === "arrow" && b.id === arrowId),
  );
  if (filtered.length === el.boundElements.length) {
    return el;
  }
  return newElementWith(el, { boundElements: filtered });
};

const addBoundArrow = (
  el: ExcalidrawElement,
  arrowId: string,
): ExcalidrawElement =>
  newElementWith(el, {
    boundElements: [...(el.boundElements ?? []), { type: "arrow", id: arrowId }],
  });

// Re-aim an existing arrow at a new start element + fixedPoint, and update
// its end fixed point to match the new branch direction.
const rebindArrow = (
  el: ExcalidrawElement,
  newStartId: string,
  startFP: [number, number],
  endFP: [number, number],
): ExcalidrawElement => {
  if (el.type !== "arrow") {
    return el;
  }
  return newElementWith(el, {
    startBinding: {
      elementId: newStartId,
      fixedPoint: startFP,
      mode: "orbit",
    },
    endBinding: el.endBinding
      ? { ...el.endBinding, fixedPoint: endFP }
      : null,
  });
};

export const reparentMindmapNode = (
  api: ExcalidrawImperativeAPI,
  dragged: MindmapNodeElement,
  newParent: MindmapNodeElement,
) => {
  const elements = api.getSceneElementsIncludingDeleted();
  const draggedData = dragged.customData[MINDMAP_DATA_KEY];

  if (draggedData.parentId === null) {
    // Cannot reparent root.
    return;
  }
  if (draggedData.parentId === newParent.customData[MINDMAP_DATA_KEY].nodeId) {
    return;
  }

  const newSide = computeSideForNewParent(dragged, newParent);
  const subtreeData = propagateSideToSubtree(elements, dragged, newSide);

  // Patch parentId on the dragged node (the root of the moving subtree).
  const draggedNew: MindmapNodeData = {
    ...subtreeData.get(dragged.id)!,
    parentId: newParent.customData[MINDMAP_DATA_KEY].nodeId,
  };
  subtreeData.set(dragged.id, draggedNew);

  const oldArrow = findIncomingArrow(elements, dragged);

  // Compute new arrow start fixed point: parent emits toward the side.
  const startFP: [number, number] = newSide === "R" ? [1, 0.5] : [0, 0.5];
  const endFP: [number, number] = newSide === "R" ? [0, 0.5] : [1, 0.5];

  // Old parent loses the arrow from its boundElements. New parent gains it.
  const oldParentNodeId = draggedData.parentId;

  let workingElements: ExcalidrawElement[] = elements.map(
    (el): ExcalidrawElement => {
      // Update mindmap nodes in the dragged subtree (parentId/side).
      const data = subtreeData.get(el.id);
      if (data && isMindmapNode(el)) {
        return newElementWith(el as ExcalidrawElement, {
          customData: { [MINDMAP_DATA_KEY]: data },
        });
      }
      return el;
    },
  );

  if (oldArrow) {
    // Re-aim the old arrow at the new parent and end fixed point.
    workingElements = workingElements.map(
      (el): ExcalidrawElement =>
        el.id === oldArrow.id
          ? rebindArrow(el, newParent.id, startFP, endFP)
          : el,
    );

    // Move the bound-arrow ref from old parent to new parent.
    workingElements = workingElements.map((el): ExcalidrawElement => {
      if (!isMindmapNode(el)) {
        return el;
      }
      const data = el.customData[MINDMAP_DATA_KEY];
      if (data.nodeId === oldParentNodeId) {
        return removeBoundArrow(el, oldArrow.id);
      }
      if (el.id === newParent.id) {
        return addBoundArrow(el, oldArrow.id);
      }
      return el;
    });
  }

  // Find the (possibly updated) root of the mindmap and run layout so the
  // dragged subtree settles into its new branch.
  const mindmapId = draggedData.mindmapId;
  const root = workingElements.find(
    (el) =>
      isMindmapNode(el) &&
      getMindmapData(el)?.parentId === null &&
      getMindmapData(el)?.mindmapId === mindmapId,
  ) as MindmapNodeElement | undefined;
  const finalElements = root
    ? applyMindmapLayout(workingElements, root)
    : workingElements;

  api.updateScene({
    elements: finalElements,
    appState: {
      selectedElementIds: { [dragged.id]: true },
    },
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
};
